<template>
  <div
    class="answer-feedback"
    :class="{ 'has-answer-feedback-block': displayBlock }"
    tabindex="-1"
  >
    <transition name="stamp">
      <div
        v-if="displayOverlay"
        class="answer-feedback-overlay"
        :class="correct ? 'correct' : 'wrong'"
        role="alert"
      >
        <FaIcon :icon="correct ? 'check' : 'times'" />
      </div>
    </transition>

    <transition name="drop">
      <div
        v-if="displayBlock"
        class="answer-feedback-block"
        :class="correct ? 'correct' : 'wrong'"
        role="alert"
      >
        <h3>
          <FaIcon icon="times" />
          {{ $t('answer-feedback.wrong-answer-heading') }}
        </h3>
        <template v-if="explanation">
          <div class="answer-feedback-content" v-html="explanation"></div>
          <TextToSpeech :text="explanation" />
        </template>
        <slot></slot>
      </div>
    </transition>
  </div>
</template>

<script>
import FaIcon from './ui/FaIcon'
import TextToSpeech from './accessibility/TextToSpeech'
import typesetMathMlMixin from '../mixins/typeset-mathml'

export default {
  mixins: [typesetMathMlMixin],
  name: 'AnswerFeedback',
  components: { TextToSpeech, FaIcon },
  props: {
    show: Boolean,
    correct: Boolean,
    explanation: String,
    allowFocus: Boolean,
  },
  data() {
    return {
      focusTimer: null,
    }
  },
  computed: {
    displayBlock() {
      return this.show && !this.correct
    },
    displayOverlay() {
      return this.show && this.correct
    },
  },
  watch: {
    allowFocus(allow) {
      if (!allow) {
        clearTimeout(this.focusTimer)
      }
    },
    displayBlock(displayBlock) {
      if (displayBlock) {
        this.$emit('answerFeedbackBlockEnabled')
        if (this.allowFocus) {
          this.focusTimer = setTimeout(() => {
            this.$el.scrollIntoView({ block: 'center', behavior: 'smooth' })
            this.focusTimer = setTimeout(() => {
              // Try to find a focus the next-button
              const nextButton = this.$el.querySelector(
                'button.next-question-button'
              )

              if (nextButton) {
                nextButton.focus()
              } else {
                // Focus the element if no next button was found, to make it easy to navigate from here
                this.$el.focus()
              }
            }, 500)
          }, 5)
        }
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.answer-feedback {
  outline: 0;

  &.has-answer-feedback-block {
    perspective: 1000px;

    .text-to-speech {
      position: absolute;
      bottom: 1em;
      right: 0.5em;
    }
  }
}

.answer-feedback-overlay {
  $correctnessIndicatorOverlaySize: 10em;

  position: absolute;
  top: 50%;
  left: 50%;
  z-index: 1;
  display: flex;
  justify-content: center;
  align-items: center;
  width: 2em;
  height: 2em;
  pointer-events: none;
  transform: translate(-50%, -50%);
  line-height: 2.1em;
  color: $white;
  border-radius: $correctnessIndicatorOverlaySize / 2;
  font-size: $correctnessIndicatorOverlaySize / 2;

  &.correct {
    background: $green;
  }

  &.wrong {
    background: $red;
  }

  &.stamp-enter-active,
  &.stamp-leave-active {
    transition: opacity 0.15s, transform 0.3s $transitionElastic;
  }

  &.stamp-enter,
  &.stamp-leave-to {
    opacity: 0;
    transform: translate(-50%, -50%) scale(0.5);
  }
}

.answer-feedback-block {
  padding: 1em 4em 1em;
  margin: 1.5em 0;
  border: 0.2em solid $frameBackgroundColor;
  border-radius: var(--theme-border-radius);
  background-color: $frameBackgroundColor;
  text-align: center;
  transition: box-shadow 0.2s ease-in-out;

  &.correct {
    border-color: $successText;

    h3 {
      color: $successText;
    }
  }

  &.wrong {
    border-color: $errorText;

    h3 {
      color: $errorText;
    }

    .answer-feedback-content {
      padding-bottom: 0.5em;
      font-weight: $fontWeightMedium;
    }
  }

  &.drop-enter-active,
  &.drop-leave-active {
    transition: box-shadow 0.2s ease-in-out, opacity 0.1s,
      transform 0.5s $transitionElastic;
    transition-delay: 0.35s;
    transform-origin: 50% 0;
  }

  &.drop-enter,
  &.drop-leave-to {
    opacity: 0;
    transform: rotateX(-90deg);
  }
}
</style>
